import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import '../styles/CurvedSlider.css';

interface CurvedSliderProps {
  images: string[];
  speed?: number;
  gap?: number;
  curve?: number;
  reverse?: boolean;
}

const CurvedSlider: React.FC<CurvedSliderProps> = ({
  images,
  speed = 30,
  gap = 10,
  curve = 12,
  reverse = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const planesRef = useRef<THREE.Mesh[]>([]);
  const timeRef = useRef(0);
  const animationIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!containerRef.current || images.length === 0) return;

    const container = containerRef.current;
    const direction = reverse ? 1 : -1;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      75,
      container.clientWidth / container.clientHeight,
      0.1,
      20
    );
    camera.position.z = 2;
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    rendererRef.current = renderer;

    // Clear previous canvas if exists
    const previousCanvas = container.querySelector('canvas');
    if (previousCanvas) {
      container.removeChild(previousCanvas);
    }
    container.appendChild(renderer.domElement);

    // Helper functions
    function getWidth(gapVal: number) {
      return 1 + gapVal / 100;
    }

    function getPlaneWidth(el: HTMLElement, cam: THREE.PerspectiveCamera) {
      const vFov = (cam.fov * Math.PI) / 180;
      const height = 2 * Math.tan(vFov / 2) * cam.position.z;
      const aspect = el.clientWidth / el.clientHeight;
      const width = height * aspect;
      return el.clientWidth / width;
    }

    // Geometry setup
    const geometry = new THREE.PlaneGeometry(1, 1, 20, 20);
    const planeSpace = getPlaneWidth(container, camera) * getWidth(gap);
    const totalImage = Math.ceil(container.clientWidth / planeSpace) + 1 + images.length;
    const initialOffset = Math.ceil(container.clientWidth / (2 * planeSpace) - 0.5);

    // Extend images array for seamless looping
    let allImages: string[] = [...images];
    for (let i = images.length; i < totalImage; i++) {
      allImages.push(images[i % images.length]);
    }

    planesRef.current = [];

    // Load textures and create planes
    const textureLoader = new THREE.TextureLoader();
    let loadedCount = 0;

    allImages.forEach((imageUrl, i) => {
      textureLoader.load(imageUrl, (texture) => {
        const material = new THREE.ShaderMaterial({
          uniforms: {
            tex: { value: texture },
            curve: { value: curve },
          },
          vertexShader: `
            uniform float curve;
            varying vec2 vertexUV;
            void main(){
              vertexUV = uv;
              vec3 newPosition = position;
              float distanceFromCenter = abs((modelMatrix * vec4(position, 1.0)).x);
              newPosition.y *= 1.0 + (curve/100.0) * pow(distanceFromCenter, 2.0);
              gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
            }
          `,
          fragmentShader: `
            uniform sampler2D tex;
            varying vec2 vertexUV;
            void main(){
              gl_FragColor = texture2D(tex, vertexUV);
            }
          `,
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.x = -1 * direction * (i - initialOffset) * getWidth(gap);
        scene.add(mesh);
        planesRef.current[i] = mesh;
        loadedCount++;
      });
    });

    // Animation loop
    function animate(currentTime: number) {
      const slideAmount = images.length;
      
      if (Math.abs(scene.position.x) >= getWidth(gap) * slideAmount) {
        timeRef.current = 0;
      }
      
      timeRef.current += direction * 0.00001 * speed;
      scene.position.x = timeRef.current * speed;
      
      renderer.render(scene, camera);
      animationIdRef.current = requestAnimationFrame(animate);
    }

    animationIdRef.current = requestAnimationFrame(animate);

    // Handle window resize
    function handleResize() {
      const width = container.clientWidth;
      const height = container.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      
      renderer.setSize(width, height);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      renderer.dispose();
      geometry.dispose();
      if (container && renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [images, speed, gap, curve, reverse]);

  return <div ref={containerRef} className="mdw-curved-slider"></div>;
};

export default CurvedSlider;
